<script>
import { useAlert } from 'dashboard/composables';
import WhatsappTemplatesAPI from 'dashboard/api/whatsappTemplates';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
const LANGUAGES = ['en_US', 'en', 'es', 'es_ES', 'pt_BR', 'fr', 'de', 'it'];
const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;

export default {
  components: {
    SettingsFieldSection,
    NextButton,
    Spinner,
    Input,
    TextArea,
    Select,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      templates: [],
      isLoading: false,
      isCreating: false,
      loadError: '',
      form: {
        name: '',
        category: 'UTILITY',
        language: 'en_US',
        body: '',
      },
      exampleValues: [],
    };
  },
  computed: {
    categoryOptions() {
      return CATEGORIES.map(value => ({ value, label: value }));
    },
    languageOptions() {
      return LANGUAGES.map(value => ({ value, label: value }));
    },
    // Meta rejects a template whose body uses {{1}} placeholders unless every
    // one of them ships a sample value, so mirror that requirement in the form.
    variableCount() {
      const matches = [...this.form.body.matchAll(VARIABLE_PATTERN)];
      const numbers = new Set(matches.map(match => Number(match[1])));
      return numbers.size;
    },
    isFormValid() {
      const nameIsValid = /^[a-z0-9_]+$/.test(this.form.name);
      const examplesFilled = this.exampleValues
        .slice(0, this.variableCount)
        .every(value => value && value.trim());

      return (
        nameIsValid &&
        this.form.body.trim().length > 0 &&
        (this.variableCount === 0 || examplesFilled)
      );
    },
    nameError() {
      if (!this.form.name) return '';
      if (/^[a-z0-9_]+$/.test(this.form.name)) return '';
      return this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.ERROR');
    },
  },
  watch: {
    variableCount: {
      immediate: true,
      handler(count) {
        // Keep one input per placeholder, preserving what is already typed.
        const next = this.exampleValues.slice(0, count);
        while (next.length < count) next.push('');
        this.exampleValues = next;
      },
    },
  },
  mounted() {
    this.fetchTemplates();
  },
  methods: {
    async fetchTemplates() {
      this.isLoading = true;
      this.loadError = '';
      try {
        const { data } = await WhatsappTemplatesAPI.getTemplates(this.inbox.id);
        this.templates = data.templates || [];
      } catch (error) {
        this.loadError =
          error?.response?.data?.error ||
          this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.ERROR');
      } finally {
        this.isLoading = false;
      }
    },
    async createTemplate() {
      if (!this.isFormValid || this.isCreating) return;

      this.isCreating = true;
      try {
        await WhatsappTemplatesAPI.createTemplate(this.inbox.id, {
          ...this.form,
          example_values: this.exampleValues.slice(0, this.variableCount),
        });
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUCCESS'));
        this.resetForm();
        await this.fetchTemplates();
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            this.$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ERROR')
        );
      } finally {
        this.isCreating = false;
      }
    },
    resetForm() {
      this.form = {
        name: '',
        category: 'UTILITY',
        language: 'en_US',
        body: '',
      };
      this.exampleValues = [];
    },
    bodyOf(template) {
      const component = (template.components || []).find(
        item => item.type === 'BODY'
      );
      return component?.text || '';
    },
    statusClass(status) {
      if (status === 'APPROVED') return 'bg-n-teal-3 text-n-teal-11';
      if (status === 'REJECTED') return 'bg-n-ruby-3 text-n-ruby-11';
      return 'bg-n-amber-3 text-n-amber-11';
    },
    // Meta sends 'NONE' rather than omitting the field when nothing was wrong.
    rejectionReason(template) {
      const reason = template.rejected_reason;
      return reason && reason !== 'NONE' ? reason : '';
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.LABEL')"
      :help-text="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.HELP_TEXT')"
    >
      <Input
        v-model="form.name"
        :placeholder="
          $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME.PLACEHOLDER')
        "
        :message="nameError"
        :message-type="nameError ? 'error' : 'info'"
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY.LABEL')"
      :help-text="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY.HELP_TEXT')"
    >
      <Select v-model="form.category" :options="categoryOptions" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE.LABEL')"
      :help-text="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE.HELP_TEXT')"
    >
      <Select v-model="form.language" :options="languageOptions" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.LABEL')"
      :help-text="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.HELP_TEXT')"
    >
      <TextArea
        v-model="form.body"
        :placeholder="
          $t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY.PLACEHOLDER')
        "
        auto-height
        resize
      />
    </SettingsFieldSection>

    <SettingsFieldSection
      v-if="variableCount > 0"
      :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES.LABEL')"
      :help-text="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EXAMPLES.HELP_TEXT')"
    >
      <div class="flex flex-col gap-2">
        <Input
          v-for="index in variableCount"
          :key="index"
          v-model="exampleValues[index - 1]"
          :placeholder="`{{${index}}}`"
        />
      </div>
    </SettingsFieldSection>

    <div>
      <NextButton
        :is-loading="isCreating"
        :disabled="!isFormValid"
        :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT')"
        @click="createTemplate"
      />
    </div>

    <div class="pt-6 border-t border-n-weak">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.TITLE') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.DESCRIPTION') }}
          </p>
        </div>
        <NextButton
          :is-loading="isLoading"
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.REFRESH')"
          @click="fetchTemplates"
        />
      </div>

      <div v-if="isLoading" class="flex justify-center py-8">
        <Spinner class="size-5 text-n-slate-11" />
      </div>

      <p v-else-if="loadError" class="py-4 text-sm text-n-ruby-11">
        {{ loadError }}
      </p>

      <p v-else-if="!templates.length" class="py-4 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.EMPTY') }}
      </p>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.COLUMNS.NAME') }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.COLUMNS.CATEGORY') }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.COLUMNS.LANGUAGE') }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.LIST.COLUMNS.STATUS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="template in templates"
              :key="template.id || template.name"
              class="border-t border-n-weak align-top"
            >
              <td class="py-3 pr-4">
                <span class="font-medium text-n-slate-12">
                  {{ template.name }}
                </span>
                <p class="mt-1 mb-0 text-n-slate-11 line-clamp-2">
                  {{ bodyOf(template) }}
                </p>
              </td>
              <td class="py-3 pr-4 text-n-slate-11">
                {{ template.category }}
              </td>
              <td class="py-3 pr-4 text-n-slate-11">
                {{ template.language }}
              </td>
              <td class="py-3 pr-4">
                <span
                  class="inline-block px-2 py-0.5 rounded-md text-xs font-medium"
                  :class="statusClass(template.status)"
                >
                  {{ template.status }}
                </span>
                <p
                  v-if="rejectionReason(template)"
                  class="mt-1 mb-0 text-xs text-n-ruby-11"
                >
                  {{ rejectionReason(template) }}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
